import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { ConnectError, Code } from '@connectrpc/connect';
import { create } from '@bufbuild/protobuf';
import {
  AuthService,
  InitiateOAuthRequestSchema,
  HandleOAuthCallbackRequestSchema,
  RefreshTokenRequestSchema,
  RevokeTokenRequestSchema,
  GetUserInfoRequestSchema,
  type UserProfile,
} from '../../generated/auth/v1/auth_pb';
import { API_BASE_URL } from '../utils/config';

// Create auth-specific transport (no auth header needed for auth endpoints)
const authTransport = createConnectTransport({
  baseUrl: API_BASE_URL,
  useBinaryFormat: false, // Use JSON format - backend expects application/json
});

// Create auth client
export const authClient = createClient(AuthService, authTransport);

/**
 * Start OAuth flow for a provider
 */
export const initiateOAuth = async (
  provider: 'google' | 'microsoft'
): Promise<{ authUrl: string; state: string }> => {
  try {
    const request = create(InitiateOAuthRequestSchema, {
      provider,
      redirectUri: `${window.location.origin}/auth/callback`,
    });

    const response = await authClient.initiateOAuth(request);

    return {
      authUrl: response.authUrl,
      state: response.state,
    };
  } catch (error) {
    console.error('Failed to initiate OAuth:', error);
    throw handleAuthError(error, 'Failed to start login process');
  }
};

/**
 * Handle OAuth callback and exchange code for tokens
 */
export const handleOAuthCallback = async (
  provider: string,
  code: string,
  state: string
): Promise<{
  accessToken: string;
  refreshToken: string;
  expiresIn: bigint;
  user: UserProfile;
}> => {
  try {
    const request = create(HandleOAuthCallbackRequestSchema, {
      provider,
      code,
      state,
    });

    const response = await authClient.handleOAuthCallback(request);

    if (!response.user) {
      throw new Error('No user profile returned from OAuth callback');
    }

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
      user: response.user,
    };
  } catch (error) {
    console.error('Failed to handle OAuth callback:', error);
    throw handleAuthError(error, 'Failed to complete login');
  }
};

/**
 * Refresh access token using refresh token
 */
export const refreshAccessToken = async (
  refreshToken: string
): Promise<{
  accessToken: string;
  refreshToken: string;
  expiresIn: bigint;
}> => {
  try {
    const request = create(RefreshTokenRequestSchema, {
      refreshToken,
    });

    const response = await authClient.refreshToken(request);

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
  } catch (error) {
    console.error('Failed to refresh token:', error);
    throw handleAuthError(error, 'Session refresh failed');
  }
};

/**
 * Revoke refresh token (logout)
 */
export const revokeToken = async (refreshToken: string): Promise<void> => {
  try {
    const request = create(RevokeTokenRequestSchema, {
      refreshToken,
    });

    await authClient.revokeToken(request);
  } catch (error) {
    console.error('Failed to revoke token:', error);
    // Don't throw on revoke errors - just log them
    // This allows logout to proceed even if backend is unavailable
  }
};

/**
 * Get current user info (requires auth header)
 */
export const getUserInfo = async (accessToken: string): Promise<UserProfile> => {
  try {
    // Create a transport with auth header for this call
    const authTransportWithToken = createConnectTransport({
      baseUrl: API_BASE_URL,
      useBinaryFormat: false, // Use JSON format - backend expects application/json
      // Add auth header via interceptor
      interceptors: [
        (next) => async (req) => {
          req.header.set('Authorization', `Bearer ${accessToken}`);
          return await next(req);
        },
      ],
    });

    const authenticatedClient = createClient(AuthService, authTransportWithToken);
    const request = create(GetUserInfoRequestSchema, {});
    const response = await authenticatedClient.getUserInfo(request);

    if (!response.user) {
      throw new Error('No user profile returned');
    }

    return response.user;
  } catch (error) {
    console.error('Failed to get user info:', error);
    throw handleAuthError(error, 'Failed to get user information');
  }
};

/**
 * Handle auth errors and convert to user-friendly messages
 */
function handleAuthError(error: unknown, defaultMessage: string): Error {
  if (error instanceof ConnectError) {
    // Check for specific error codes
    if (error.code === Code.Unauthenticated) {
      return new Error('Session expired. Please log in again.');
    }
    if (error.code === Code.PermissionDenied) {
      return new Error('Access denied. Please check your permissions.');
    }
    if (error.code === Code.InvalidArgument) {
      return new Error('Invalid request. Please try again.');
    }

    // Check for network errors in message
    const message = error.message || '';
    if (
      message.includes('Load failed') ||
      message.includes('Failed to fetch') ||
      message.includes('NetworkError')
    ) {
      return new Error(
        'Network error. Please check your connection and try again.'
      );
    }

    // Return error message if available
    if (error.message) {
      return new Error(error.message);
    }
  }

  // Return default message for unknown errors
  return new Error(
    error instanceof Error ? error.message : defaultMessage
  );
}
